import { createPortal, } from 'react-dom';
import styled from 'styled-components';
import { useEffect, useRef, type Ref, } from 'react';
import { atom, useAtom, useSetAtom, } from 'jotai';
import { create, } from 'zustand';
import { gsap, } from 'gsap';
import { Text, } from "@visx/text";

export declare type ToolTipState = {
	active: boolean,
	message: string | string[] | null,
	x: number,
	xOffset: number,
	y: number,
	yOffset: number,
	since: number,
	open: ( x: number, y: number, message: string, options?: ToolTipOptions) => void,
	close: () => void,
	reset: () => void,
};

export declare type ToolTipOptions = {
	yOffset?: number;
	xOffset?: number;
};

export const useToolTip = create<ToolTipState>((set, get) => ({
	active: false,
	message: null,
	x: -1,
	y: -1,
	yOffset: 0,
	xOffset: 0,
	since: 0,
	open: ( x: number, y: number, message: string, options: ToolTipOptions = {}) => {
		set(() => ({
			active: true,
			message,
			x: x,
			y: y,
			yOffset: options.yOffset || 0,
			xOffset: options.xOffset || 0,
			since: Date.now(),
		}));
	},
	close: () => {
			set((state: any) => ({ active: false, }));
	},
	reset: () => {
		set((state: any) => ({ ...state, active: false, message: null, x: -1, y: -1, }));
	}
}));

export declare type ToolTipProps = {
	target?: string;
};

export const ToolTip = ({ target, }: ToolTipProps) => {
	const { active, message, x, y, yOffset, xOffset, } = useToolTip(state => state);
	const messageLines = (Array.isArray(message) ? message : message ? message.split("\n") : []).filter(s => s.length);

	const ref = useRef<HTMLDivElement>(null);
	const tref = useRef(null);
	const textGroupRef = useRef<SVGGElement>(null);
	const rectRef = useRef<SVGRectElement>(null);
	const svgFramerRef = useRef<SVGRectElement>(null);
	const arrowRef = useRef<SVGPolygonElement>(null);
	useEffect(() => {
		if(ref.current) {
			gsap.set(ref.current, { opacity: 0, });
		}
	}, [])

	useEffect(() => {
		if(ref.current) {
			gsap.killTweensOf(ref.current);
			if(active) {
				setTimeout(() => {
					if(ref.current && textGroupRef.current) {
						const pad = 10;
						const tpad = 10;
						const padExtents = pad*2;
						const tpadExtents = tpad*2;
						const lineSpacing = 5;
						const lines = Array.from(textGroupRef.current.children) as SVGTextElement[];
						if(!lines.length) return;

						const width = lines.reduce((acc, child) => acc + child.getBoundingClientRect().width > acc ? child.getBoundingClientRect().width : acc, 0) ?? 0;
						const height = lines.reduce((acc, child) => acc + (child as SVGTextElement).getBoundingClientRect().height + lineSpacing, 0) ?? 0;
						const sheight = height  / lines.length;
						let lineNum = 0;
						for(const line of lines) {
							gsap.to(line, { y: pad+tpad+(sheight*lineNum), x: pad+tpad, duration: 0.35, ease: "power2.out", });
							lineNum++;
						}
						ref.current.style.minWidth = width+padExtents+tpadExtents+"px";
						ref.current.style.minHeight = height+padExtents+tpadExtents+"px";
						gsap.fromTo(ref.current,
							{ opacity: 0, top: y + yOffset + pad, left: x - (width/2) - xOffset - tpad, width: width+padExtents, height: height+padExtents },
							{ opacity: 1, duration: 1, });
						gsap.to(arrowRef.current, { x: (width+padExtents)/2, y: 0, });
						gsap.to(svgFramerRef.current, { width: width+padExtents+tpadExtents, height: height+padExtents+tpadExtents });
						gsap.to(rectRef.current, { width: width+tpadExtents, x: tpad, y: tpad, height: (height-(lineSpacing*2))+tpadExtents, });
						
					}
				}, 0);
			}
			else {
				gsap.to(ref.current, { opacity: 0, duration: 2, });
			}
		}
	}, [ active, x, y, message, ]);

	return createPortal(
		<ToolTipContainer ref={ref}>
			<svg>
				<defs>
					<filter id="tooltip-dropshadow" x="-20%" y="-20%" width="140%" height="140%">
						<feDropShadow dx="0" dy="0" stdDeviation="5" floodColor="rgba(0, 0, 0, 0.3)" />
					</filter>
				</defs>
				<rect ref={svgFramerRef} fill="transparent" />
				<rect ref={rectRef} fill="black" rx={5} filter="url(#tooltip-dropshadow)" />
				<polygon
					ref={arrowRef}
					points="10,0 20,10 0,10"
					fill="black"
					filter="url(#tooltip-dropshadow)"
				/>
				<g ref={textGroupRef}>
				{
					messageLines.map((message, index) => (
						<Text key={index} verticalAnchor="start" textAnchor="start" width={150} fill="white" fontSize=".95em" fontFamily="Inter" innerTextRef={tref}>{message}</Text>
					))
				}
				</g>
			</svg>
		</ToolTipContainer>,
		document.body.querySelector("#overlay")
	);
};

const ToolTipContainer = styled.div`
	position: absolute;
	pointer-events: none;
	display: flex;
	flex: 1;
`;
